import type {
  Adjustment,
  Company,
  CompanySettings,
  EditGrant,
  EditLogEntry,
  Employee,
  LangCode,
  LeaveRequest,
  NotificationEvent,
  OrlofEntry,
  OrlofEntryKind,
  PayrollRun,
  OvertimeConfirmation,
  RequestStatus,
  RequestType,
  Shift,
  Timesheet,
  TimesheetStatus,
} from '@klukka/core';

/**
 * The wire contract between the two portals and the backend.
 *
 * Both adapters — `LocalApi` (browser storage, for local development and
 * demos) and `HttpApi` (the Azure Functions API) — implement this interface,
 * so no component ever knows which one it is talking to.
 *
 * Reads return a *snapshot*: everything one portal needs for one render pass.
 * Writes return the fresh snapshot too, so a mutation never leaves the UI
 * guessing what changed.
 */

export type Portal = 'employee' | 'employer';

export interface OtpChallenge {
  challengeId: string;
  maskedPhone: string;
  /** True the very first time an account signs in — kennitala alone is enough. */
  firstLogin: boolean;
  /** Only populated when the backend runs in dev/demo OTP mode. */
  devCode?: string;
}

export interface Session {
  token: string;
  portal: Portal;
  companyId: string;
  /** Set for employee sessions; null for administrators. */
  empId: string | null;
  name: string;
  expiresAt: string;
}

export interface EmployeeSnapshot {
  serverToday: string;
  company: Company;
  employee: Employee;
  /** The open shift, if the worker is clocked in right now. */
  shift: Shift | null;
  grant: EditGrant | null;
  requests: LeaveRequest[];
  timesheets: Timesheet[];
  adjustments: Adjustment[];
  edits: EditLogEntry[];
  notifications: NotificationEvent[];
  /** The vacation account. Balances are derived from this, never stored. */
  orlof: OrlofEntry[];
  /**
   * Approved periods this employee can download a payslip for, newest first.
   * The amounts are recalculated with the list, so they always match the PDF.
   */
  payslips: PayslipSummary[];
}

export interface EmployerSnapshot {
  serverToday: string;
  company: Company;
  /** Includes deactivated employees — the grid shows them greyed out. */
  employees: Employee[];
  openShifts: Shift[];
  requests: LeaveRequest[];
  timesheets: Timesheet[];
  adjustments: Adjustment[];
  editLog: EditLogEntry[];
  editGrants: Record<string, EditGrant>;
  overtimeConfirmations: OvertimeConfirmation[];
  /** Every employee's vacation account, for the liability view. */
  orlofEntries: OrlofEntry[];
  /** Pay periods that have been opened or approved, newest first. */
  payrollPeriods: PayrollPeriod[];
  /** Notification history, keyed by period. */
  payslipDeliveries: Record<string, PayslipDelivery[]>;
}

/** Approval state for one pay period. The money is never stored here. */
export interface PayrollPeriod {
  id: string;
  periodKey: string;
  periodStart: string;
  periodEnd: string;
  payoutDate: string;
  status: PayrollPeriodStatus;
  approvedBy: string | null;
  approvedAt: string | null;
  reopenedBy: string | null;
  reopenedAt: string | null;
  createdBy: string;
  createdAt: string;
}

export type PayrollPeriodStatus = 'draft' | 'approved';

/** What was sent, to which address, and whether it got there. */
export interface PayslipDelivery {
  empId: string;
  address: string;
  status: 'sent' | 'failed';
  /** Failure category — never the provider's raw error. */
  detail: string;
  sentAt: string;
  sentBy: string;
}

/**
 * A calculated period with its approval state.
 *
 * `run` is computed fresh on every call rather than read back, so the console
 * can never show figures that have gone stale behind a correction.
 */
export interface PayrollPeriodView {
  period: PayrollPeriod;
  run: PayrollRun;
  deliveries: PayslipDelivery[];
}

/** Outcome of one notification attempt, per employee. */
export interface PayslipNotifyResult {
  empId: string;
  ok: boolean;
  reason?: string;
}

/** One line in an employee's own list of payslips. */
export interface PayslipSummary {
  periodKey: string;
  periodStart: string;
  periodEnd: string;
  payoutDate: string;
  approvedAt: string;
  gross: number;
  net: number;
}

export interface NewEmployeeInput {
  name: string;
  kennitala: string;
  monthlyWage: number;
  role?: string;
  lang?: LangCode;
}

export interface NewRequestInput {
  type: RequestType;
  from: string;
  fromTime: string;
  to: string;
  toTime: string;
  note: string;
}

export interface ImportResult {
  imported: number;
  skipped: number;
  errors: string[];
}

export interface HourCorrectionInput {
  empId: string;
  date: string;
  /** Signed delta against the day's logged hours, e.g. `-1.5`. */
  deltaHours: number;
  note: string;
}

/** Fields an employee is allowed to change about themselves. */
export type SelfServicePatch = Partial<
  Pick<
    Employee,
    | 'phone' | 'email' | 'address' | 'bank' | 'union' | 'pensionFund'
    | 'creditUtil' | 'priorUnusedCredit' | 'extraPensionPct' | 'extraPensionFund' | 'lang'
  >
>;

/** Fields the employer may change. Wage and identity live here, not on the phone. */
export type EmployeePatch = Partial<Omit<Employee, 'id' | 'companyId' | 'worklog' | 'inactive'>>;

/** A vacation-account line an administrator may write by hand. */
export interface OrlofEntryInput {
  empId: string;
  kind: Exclude<OrlofEntryKind, 'accrual'>;
  date: string;
  /** Always positive — `kind` decides the direction. */
  days: number;
  note: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface KlukkaApi {
  // ---- authentication ----
  /** Look the kennitala up and start an OTP challenge for the given portal. */
  requestOtp(kennitala: string, portal: Portal): Promise<OtpChallenge>;
  verifyOtp(challengeId: string, code: string): Promise<Session>;
  /** Complete a first-ever login, which needs no code. */
  completeFirstLogin(challengeId: string): Promise<Session>;
  currentSession(): Promise<Session | null>;
  logout(): Promise<void>;

  // ---- employee portal ----
  getEmployeeSnapshot(): Promise<EmployeeSnapshot>;
  clockIn(): Promise<EmployeeSnapshot>;
  clockOut(): Promise<EmployeeSnapshot>;
  confirmOvertime(): Promise<EmployeeSnapshot>;
  updateMyProfile(patch: SelfServicePatch): Promise<EmployeeSnapshot>;
  editMyHours(date: string, hours: number): Promise<EmployeeSnapshot>;
  signTimesheet(input: { periodKey: string; label: string; hours: number; signature: string }): Promise<EmployeeSnapshot>;
  createRequest(input: NewRequestInput): Promise<EmployeeSnapshot>;
  acknowledgeNotification(id: string): Promise<EmployeeSnapshot>;

  // ---- employer portal ----
  getEmployerSnapshot(): Promise<EmployerSnapshot>;
  updateSettings(patch: Partial<CompanySettings>): Promise<EmployerSnapshot>;
  createEmployee(input: NewEmployeeInput): Promise<EmployerSnapshot>;
  updateEmployee(empId: string, patch: EmployeePatch): Promise<EmployerSnapshot>;
  deactivateEmployee(empId: string): Promise<EmployerSnapshot>;
  reactivateEmployee(empId: string): Promise<EmployerSnapshot>;
  /** Only permitted for employees with no recorded history. */
  deleteEmployee(empId: string): Promise<EmployerSnapshot>;
  setEditGrant(empId: string, grant: Omit<EditGrant, 'empId' | 'by' | 'ts'> | null): Promise<EmployerSnapshot>;
  /**
   * Clock a worker out at the time their shift really ended — for a phone that
   * died, a worker who left, or a shift left running. The hours land on the day
   * the shift started and the audit trail names the administrator.
   */
  clockOutEmployee(empId: string, at: string): Promise<EmployerSnapshot>;
  decideRequest(id: string, status: RequestStatus): Promise<EmployerSnapshot>;
  decideTimesheet(id: string, status: TimesheetStatus): Promise<EmployerSnapshot>;
  importWorklogs(csv: string): Promise<{ result: ImportResult; snapshot: EmployerSnapshot }>;
  createAdjustment(input: { empId: string; date: string; amount: number; note: string }): Promise<EmployerSnapshot>;
  /**
   * Write a line to an employee's vacation account: the balance they arrived
   * with, a payout, or a correction. Accrual is posted by the system and is
   * not accepted here. Nothing is ever edited or removed — a mistake is fixed
   * by posting its opposite.
   */
  addOrlofEntry(input: OrlofEntryInput): Promise<EmployerSnapshot>;

  // ---- payroll ----
  /**
   * Calculate a period and return it with its approval state.
   *
   * Recalculating is the same call: nothing is stored but the fact that the
   * period has been opened, so this is also how the console refreshes after
   * correcting somebody's hours.
   */
  payrollPeriod(period: { periodStart: string; periodEnd: string }): Promise<PayrollPeriodView & { snapshot: EmployerSnapshot }>;
  /** Mark the period reviewed. Employees can see their payslip from this point. */
  approvePayrollPeriod(period: { periodStart: string; periodEnd: string }): Promise<PayrollPeriodView & { snapshot: EmployerSnapshot }>;
  /** Take it back to draft so the figures can be changed. Hides it from employees again. */
  reopenPayrollPeriod(period: { periodStart: string; periodEnd: string }): Promise<PayrollPeriodView & { snapshot: EmployerSnapshot }>;
  /** The four-page report. */
  payrollReportPdf(period: { periodStart: string; periodEnd: string }, lang: 'is' | 'en'): Promise<Uint8Array>;
  /** Every payslip in one document, for review. */
  payslipBatchPdf(period: { periodStart: string; periodEnd: string }): Promise<Uint8Array>;
  payslipPdf(period: { periodStart: string; periodEnd: string }, empId: string): Promise<Uint8Array>;
  /**
   * Email employees a link to their payslip in the portal.
   *
   * A link rather than an attachment: what they open is always the current
   * calculation, and wage data never sits in an inbox. Omit `empId` to notify
   * everybody on the period.
   */
  notifyPayslips(period: { periodStart: string; periodEnd: string }, empId?: string): Promise<{
    results: PayslipNotifyResult[];
    period: PayrollPeriod;
    deliveries: PayslipDelivery[];
    snapshot: EmployerSnapshot;
  }>;
  /** Whether this deployment can send email at all. */
  emailAvailable(): Promise<boolean>;

  // ---- work reports ----
  /** One employee's day-by-day sheet for a period. */
  timesheetPdf(period: { periodStart: string; periodEnd: string }, empId: string, lang: 'is' | 'en'): Promise<Uint8Array>;
  /** Everyone's sheet for a period, one page each. */
  timesheetBatchPdf(period: { periodStart: string; periodEnd: string }, lang: 'is' | 'en'): Promise<Uint8Array>;

  // ---- payroll and reports, employee side ----
  /** The signed-in employee's own payslip for an approved period. */
  myPayslipPdf(periodKey: string): Promise<Uint8Array>;
  /** The signed-in employee's own work report, in their own language. */
  myTimesheetPdf(period: { periodStart: string; periodEnd: string }): Promise<Uint8Array>;
  deleteAdjustment(id: string): Promise<EmployerSnapshot>;
  correctHours(input: HourCorrectionInput): Promise<{ oldH: number; newH: number; snapshot: EmployerSnapshot }>;
  deleteHourCorrection(id: string): Promise<EmployerSnapshot>;
}
